// app/javascript/controllers/push_prompt_controller.js
// Auto-triggers native browser push notification permission
// No banner - just the native browser prompt
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    context: { type: String, default: 'default' }
  }

  connect() {
    console.log('[Push] Prompt controller connected, will auto-prompt in 1 second...')
    // Small delay to let page settle, then auto-trigger
    setTimeout(() => this.autoPrompt(), 1000)
  }

  async autoPrompt() {
    // Check if push is supported
    if (!this.isPushSupported()) {
      console.log('[Push] Not supported on this browser')
      return
    }

    // Check if already subscribed
    if (await this.isAlreadySubscribed()) {
      console.log('[Push] Already subscribed')
      return
    }

    // Check if user already denied or dismissed
    if (this.shouldSkipPrompt()) {
      console.log('[Push] Skipping prompt (denied or dismissed)')
      return
    }

    // Check current permission state
    const currentPermission = Notification.permission

    if (currentPermission === 'denied') {
      // User blocked notifications, don't bother asking
      localStorage.setItem('push_prompt_denied', 'true')
      return
    }

    if (currentPermission === 'granted') {
      // Permission already granted, just subscribe
      await this.subscribe()
      return
    }

    // Permission is 'default' - ask for permission
    await this.requestPermissionAndSubscribe()
  }

  isPushSupported() {
    return 'serviceWorker' in navigator &&
           'PushManager' in window &&
           'Notification' in window
  }

  async isAlreadySubscribed() {
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      return !!subscription
    } catch (error) {
      return false
    }
  }

  shouldSkipPrompt() {
    // Skip if user previously denied
    if (localStorage.getItem('push_prompt_denied') === 'true') {
      return true
    }

    // Skip if dismissed recently (within 7 days)
    const dismissedAt = localStorage.getItem('push_prompt_dismissed')
    if (dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24)
      if (daysSinceDismissed < 7) {
        return true
      }
    }

    return false
  }

  async requestPermissionAndSubscribe() {
    try {
      const permission = await Notification.requestPermission()

      if (permission === 'granted') {
        await this.subscribe()
      } else if (permission === 'denied') {
        localStorage.setItem('push_prompt_denied', 'true')
      } else {
        // 'default' means dismissed - remember for 7 days
        localStorage.setItem('push_prompt_dismissed', Date.now().toString())
      }
    } catch (error) {
      console.error('[Push] Permission request failed:', error)
    }
  }

  async subscribe() {
    try {
      const registration = await navigator.serviceWorker.ready

      // Get VAPID key
      const response = await fetch('/api/push_subscriptions/vapid_public_key')
      if (!response.ok) {
        throw new Error('Could not get VAPID key')
      }
      const { publicKey } = await response.json()

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(publicKey)
      })

      // Save to server
      const json = subscription.toJSON()
      await fetch('/api/push_subscriptions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          endpoint: json.endpoint,
          p256dh_key: json.keys.p256dh,
          auth_key: json.keys.auth
        })
      })

      console.log('[Push] Successfully subscribed!')
    } catch (error) {
      console.error('[Push] Subscription failed:', error)
    }
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }

    return outputArray
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }
}
